import React, { useEffect, useRef, useState } from 'react';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { FiClock, FiCheckCircle } from 'react-icons/fi';
import { Rocket } from 'lucide-react';
import { formatDuration } from '../formatDuration';

// Isolado num componente próprio pra que o tick de 1s não force o resto da
// sidebar (incluindo o gráfico de pizza) a re-renderizar — sem isso, o
// Highcharts recebia um objeto de opções novo a cada segundo e replay a
// animação de entrada, piscando visivelmente.
function DurationClock({ dailyAnswer }) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    const computeElapsed = () => {
      if (!dailyAnswer) {
        setElapsedMs(0);
        return;
      }
      const runningMs = dailyAnswer.status === 'played' ? (moment().unix() - moment(dailyAnswer.currentTimeStart).unix()) * 1000 : 0;
      setElapsedMs((dailyAnswer.duration || 0) + Math.max(runningMs, 0));
    };

    computeElapsed();
    clearInterval(tickRef.current);
    if (dailyAnswer?.status === 'played') {
      tickRef.current = setInterval(computeElapsed, 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [dailyAnswer]);

  return (
    <span
      className="text-sm font-extrabold"
      style={{ color: '#222731' }}
    >
      {formatDuration(elapsedMs)}
    </span>
  );
}

export function SidebarPlayful({ book, answeredCount, totalCount, lastAnswerSubmitted, onExit }) {
  const client = useClient();
  const [dailyAnswer, setDailyAnswer] = useState(null);
  const didAutoStart = useRef(false);

  useEffect(() => {
    const fetchDailyAnswer = async () => {
      const { data, error } = await client.fetch('daily_grouped_answers/last');
      if (error) return;
      // Play automático ao abrir a página: se há sessão e ela não está rodando,
      // dá play uma única vez. Depois disso respeita o Pausar/Continuar manual.
      if (data?.id && !didAutoStart.current && data.status !== 'played') {
        didAutoStart.current = true;
        const { data: played, error: playError } = await client.mutate(['daily_grouped_answers', data.id], { status: 'played' });
        setDailyAnswer(playError ? data : played);
        return;
      }
      if (data?.id) didAutoStart.current = true;
      setDailyAnswer(data);
    };
    fetchDailyAnswer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAnswerSubmitted]);

  const isPlaying = dailyAnswer?.status === 'played';

  const handlePauseOrResume = async () => {
    if (!dailyAnswer) return;
    const { data, error } = await client.mutate(['daily_grouped_answers', dailyAnswer.id], { status: isPlaying ? 'paused' : 'played' });
    if (!error) setDailyAnswer(data);
  };

  const handleSaveAndExit = async () => {
    if (dailyAnswer) {
      await client.mutate(['daily_grouped_answers', dailyAnswer.id], { status: 'paused' });
    }
    onExit();
  };

  const hitPercentage = dailyAnswer?.statistics?.hitPercentage || 0;
  const taxonomies = book?.taxonomies || [];

  return (
    <aside className="space-y-4 rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <h2
            className="text-lg font-extrabold leading-snug"
            style={{ color: '#222731' }}
          >
            {book?.title}
          </h2>
          {taxonomies.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {taxonomies.slice(0, 2).map(tax => (
                <span
                  key={tax.id}
                  className="rounded-full bg-[var(--color-bg-teal-soft)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-primary)]"
                >
                  {tax.name}
                </span>
              ))}
              {taxonomies.length > 2 && <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-400">+{taxonomies.length - 2}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 rounded-[16px] bg-white p-4 shadow-card">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          style={{ background: 'var(--color-bg-lilac)' }}
        >
          <Rocket
            className="h-5 w-5"
            style={{ color: 'var(--color-secondary)' }}
          />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block text-xs font-bold uppercase tracking-wide text-neutral-400">Aproveitamento</span>
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full"
              style={{ width: `${hitPercentage}%`, background: 'var(--gradient-btn-continue)' }}
            />
          </div>
          <span
            className="text-xs font-extrabold"
            style={{ color: '#222731' }}
          >
            {hitPercentage}%
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4 rounded-[16px] bg-white p-4 shadow-card">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          style={{ background: 'var(--color-bg-teal-soft)' }}
        >
          <FiClock
            data-testid="clock-icon"
            className="h-5 w-5 text-[var(--color-primary)]"
          />
        </span>
        <div className="flex flex-1 items-center justify-between gap-2">
          <div>
            <span className="block text-xs font-bold uppercase tracking-wide text-neutral-400">Duração</span>
            <DurationClock dailyAnswer={dailyAnswer} />
          </div>
          <button
            type="button"
            onClick={handlePauseOrResume}
            className="rounded-full border border-neutral-lighter bg-white px-4 py-1.5 text-xs font-bold text-neutral-dark transition-colors hover:border-[var(--color-secondary)] hover:text-[var(--color-secondary)]"
          >
            {isPlaying ? 'Pausar' : 'Continuar'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 rounded-[16px] bg-white p-4 shadow-card">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          style={{ background: 'var(--color-bg-mission-soft)' }}
        >
          <FiCheckCircle className="h-5 w-5 text-[var(--color-mission-medium)]" />
        </span>
        <div>
          <span className="block text-xs font-bold uppercase tracking-wide text-neutral-400">Questões</span>
          <span
            className="text-sm font-extrabold"
            style={{ color: '#222731' }}
          >
            {answeredCount}/{totalCount} respondidas
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSaveAndExit}
        className="group/btn relative w-full overflow-hidden rounded-full py-3 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
        style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
        />
        <span className="relative">Salvar e sair</span>
      </button>
    </aside>
  );
}
